import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { useT } from '../messages';

/**
 * **"Add a note"** — one tap, a focused field, and a microphone where the
 * browser has one (B1; `docs/lovespa-discovery.md` §3).
 *
 * The owner's second-highest priority, in her own words: *"his son's birthday is
 * tomorrow"*, *"can't use the strawberry on her"* — about thirty per cent of
 * visits get one, and today they go in her head. The thing that decides whether
 * a note gets written is how far it is from the moment she thought of it, which
 * is why this is a component rather than a screen: it is dropped onto the
 * appointment sheet and onto the checkout-complete state, and both are the
 * moment.
 *
 * ## It appends a dated line; it never replaces
 *
 * `clients.notes` is one text column and the write is `PATCH /v1/clients/{id}`,
 * which replaces it. So the new line is prepended to what is already there with
 * the date in front — `2026-09-19 — son's birthday tomorrow` — and the previous
 * text is carried through untouched. Newest first, because the question being
 * asked is "what do I need to know today".
 *
 * The date is the **tenant's**, not the device's. An iPad that travels must not
 * start stamping yesterday.
 *
 * The append is computed from the **mirrored** row, which is the honest limit of
 * an offline write and is stated here rather than discovered later: two people
 * adding a note to the same client while both are offline will replay in outbox
 * order and the second will carry the first's text only if the first had already
 * settled. That is the same last-writer-wins `updateClient` has always had — the
 * screen is not making it worse — and it is why this appends one short line
 * rather than opening a whole-notes editor.
 *
 * ## Dictation
 *
 * `webkitSpeechRecognition` where it exists, and **no button at all where it
 * does not**. On iOS Safari — the device this actually runs on — the keyboard's
 * own microphone already dictates into any text field, so the plain field is the
 * baseline and this is genuinely polish. A button that did nothing on the one
 * platform that matters would be worse than no button.
 */
export function AddClientNote({
  clientId,
  clientName,
  /** Absent means "read it out of the mirror", which is what both callers want. */
  existingNotes,
  onSaved,
}: {
  clientId: string;
  clientName?: string | undefined;
  existingNotes?: string | null | undefined;
  onSaved?: (() => void) | undefined;
}): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const field = useRef<HTMLTextAreaElement | null>(null);

  const session = useLive(() => runtime.mirror.session(), null);
  const mirrored = useLive(async () => (await runtime.mirror.client(clientId)) ?? null, null, [
    clientId,
  ]);

  // One tap should land the caret in the box. Without this the owner taps
  // "Add a note" and then has to tap again to type, which is the tap that makes
  // people stop bothering.
  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  const save = (): void => {
    const line = text.trim();
    if (line === '') return;
    const timezone = session?.tenant.timezone ?? 'UTC';
    const previous = existingNotes ?? mirrored?.notes ?? '';
    void actions.updateClient({
      clientId,
      name: clientName ?? mirrored?.display_name ?? '',
      // `runtime.now()` rather than `Date.now()`: the app has one clock and it
      // is injectable, which is what lets a test assert the stamped date at all.
      changes: { notes: appendNote(previous, line, today(timezone, runtime.now())) },
    });
    setText('');
    setOpen(false);
    onSaved?.();
  };

  if (!open) {
    return (
      <button type="button" className="link-button" onClick={() => setOpen(true)}>
        {t('note.add')}
      </button>
    );
  }

  return (
    <div className="editor editor--note">
      <textarea
        ref={field}
        rows={2}
        value={text}
        aria-label={t('note.add')}
        placeholder={t('note.placeholder')}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="row-actions">
        {speechAvailable() ? (
          <button
            type="button"
            className={`button button--ghost${listening ? ' is-listening' : ''}`}
            aria-pressed={listening}
            onClick={() =>
              dictate(
                (heard) => setText((current) => (current === '' ? heard : `${current} ${heard}`)),
                setListening,
              )
            }
          >
            {listening ? t('note.listening') : t('note.dictate')}
          </button>
        ) : null}
        <button type="button" className="link-button" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={text.trim() === ''}
          onClick={save}
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

/**
 * The new line, dated, above whatever was there.
 *
 * Exported because it is the only part of this component with a rule in it, and
 * a rule about how a business's own notes are assembled deserves a test that
 * does not need a renderer.
 */
export function appendNote(previous: string, line: string, isoDay: string): string {
  const entry = `${isoDay} — ${line}`;
  const before = previous.trim();
  return before === '' ? entry : `${entry}\n${before}`;
}

/** `YYYY-MM-DD` in the tenant's timezone. A travelling iPad must not stamp yesterday. */
function today(timezone: string, nowMs: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(nowMs));
}

/* ------------------------------------------------------------------ */
/* The microphone                                                      */
/* ------------------------------------------------------------------ */

/**
 * The Web Speech API, as much of it as this needs, typed here rather than
 * pulled in as a dependency.
 *
 * It is `webkitSpeechRecognition` on every browser that has it worth having, and
 * unprefixed on none of the ones this app runs in. Feature-detected at the call
 * site, so a browser without it draws no button at all.
 */
interface SpeechResultLike {
  0: { transcript: string };
}
interface SpeechEventLike {
  results: ArrayLike<SpeechResultLike>;
}
interface RecognitionLike {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  onresult: ((event: SpeechEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}
type RecognitionConstructor = new () => RecognitionLike;

function recognitionClass(): RecognitionConstructor | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as {
    webkitSpeechRecognition?: RecognitionConstructor;
    SpeechRecognition?: RecognitionConstructor;
  };
  return w.webkitSpeechRecognition ?? w.SpeechRecognition;
}

export function speechAvailable(): boolean {
  return recognitionClass() !== undefined;
}

/**
 * Listen once and hand back what was heard.
 *
 * Deliberately one utterance rather than a running transcription: she is saying
 * one sentence about one person, and a session that stayed open would keep the
 * microphone on a device sitting face-up on a counter all day. Every failure
 * path — permission refused, no speech, a browser that changed its mind — ends
 * in `onend`, which puts the button back and leaves whatever is already typed
 * alone. Dictation is an alternative to typing, never a prerequisite for it.
 */
function dictate(onHeard: (text: string) => void, setListening: (on: boolean) => void): void {
  const Recognition = recognitionClass();
  if (Recognition === undefined) return;
  const recognition = new Recognition();
  // The device's language, so a bilingual counter is dictated to in the language
  // the person is already working in (`docs/i18n-design.md` §2).
  recognition.lang = typeof navigator === 'undefined' ? 'en-US' : navigator.language;
  recognition.interimResults = false;
  recognition.maxAlternatives = 1;
  recognition.onresult = (event): void => {
    const heard = event.results[0]?.[0].transcript.trim() ?? '';
    if (heard !== '') onHeard(heard);
  };
  recognition.onerror = (): void => setListening(false);
  recognition.onend = (): void => setListening(false);
  setListening(true);
  try {
    recognition.start();
  } catch {
    // Already running, or blocked by the platform. Either way the button goes
    // back and the field is still there.
    setListening(false);
  }
}
